import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cars, configOptions } from '../data/mockData';
import StatBar from '../components/configurator/StatBar';
import AsciiPreview from '../components/configurator/AsciiPreview';
import BuildYieldCalculator from '../components/BuildYieldCalculator';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const CATEGORIES = [
  { key: 'engine', label: '01 // Data Generation Layer', sub: 'AI Core', icon: FiIcons.FiCpu, accent: 'cyan' },
  { key: 'storage', label: '02 // Local Storage System', sub: 'Database', icon: FiIcons.FiDatabase, accent: 'cyan' },
  { key: 'validation', label: '03 // Privacy Sanitization', sub: 'Validation', icon: FiIcons.FiShield, accent: 'fuchsia' },
  { key: 'interface', label: '04 // Sovereign Interface', sub: 'Interface', icon: FiIcons.FiMonitor, accent: 'amber' },
];

export default function Build() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [deployed, setDeployed] = useState(false);
  const [config, setConfig] = useState({
    engine: configOptions.engines[0],
    storage: configOptions.storage[0],
    validation: configOptions.validation[0],
    interface: configOptions.interface[0],
  });

  useEffect(() => {
    const found = cars.find(c => c.id === id);
    if (!found) navigate('/showroom');
    else setCar(found);
  }, [id, navigate]);

  if (!car) return <div className="min-h-screen flex items-center justify-center font-mono text-cyan-400">LOADING_CORE...</div>;

  const currentStats = {
    speed: car.stats.speed + (config.engine.boost.speed || 0) + (config.interface.boost.speed || 0),
    handling: car.stats.handling + (config.storage.boost.handling || 0) + (config.validation.boost.handling || 0) + (config.interface.boost.handling || 0),
    aiCore: car.stats.aiCore + (config.engine.boost.aiCore || 0) + (config.storage.boost.aiCore || 0),
  };

  const total = car.basePrice + config.engine.price + config.storage.price + config.validation.price + config.interface.price;

  const accentHex = {
    cyan: '#06b6d4', fuchsia: '#d946ef', amber: '#ffaa00'
  }[config.interface.id === 'tauri' ? 'amber' : config.engine.id === 'neuro' ? 'fuchsia' : 'cyan'];

  return (
    <div className="min-h-screen pt-24 flex flex-col lg:flex-row">
      {/* Preview */}
      <div className="lg:w-3/5 relative min-h-[60vh] lg:min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-8 overflow-hidden border-r border-zinc-800/50">
        <div
          className="absolute inset-0 opacity-20 transition-colors duration-700 pointer-events-none"
          style={{ background: `radial-gradient(circle at center, ${accentHex} 0%, transparent 70%)` }}
        />
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />

        <div className="relative z-10 w-full max-w-3xl">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-zinc-900/80 backdrop-blur border border-zinc-800 px-4 py-2 rounded text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
              Status: <span className="text-cyan-400">Node_Config_Mode</span>
            </div>
            <div className="bg-zinc-900/80 backdrop-blur border border-amber-500/30 px-4 py-2 rounded text-[10px] font-mono tracking-widest text-amber-400 uppercase">
              CR {total.toLocaleString()}
            </div>
          </div>

          <AsciiPreview ascii={car.ascii} color={accentHex} />

          <div className="mt-6 bg-zinc-900/40 p-5 rounded-lg border border-zinc-800/50 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiActivity} /> Telemetry
              </span>
              <span className="text-[10px] font-mono text-rose-400">ZERO external calls</span>
            </div>
            <StatBar label="Data Velocity" value={currentStats.speed} colorClass="bg-cyan-500" />
            <StatBar label="Privacy Shield" value={currentStats.handling} colorClass="bg-emerald-500" />
            <StatBar label="AI Core Logic" value={currentStats.aiCore} max={150} colorClass="bg-fuchsia-500" />
          </div>

          {/* Yield Calculator */}
          <BuildYieldCalculator config={config} />
        </div>
      </div>

      {/* Controls */}
      <div className="lg:w-2/5 bg-zinc-950 flex flex-col lg:h-[calc(100vh-5rem)] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800/50 bg-zinc-900/30 lg:sticky lg:top-0 z-20 backdrop-blur-md">
          <div className="text-[10px] text-amber-400 tracking-widest uppercase mb-1">{car.class}</div>
          <h2 className="text-2xl font-black uppercase tracking-tight text-zinc-100">{car.name}</h2>
          <div className="text-xs text-zinc-500 mt-1 font-mono">
            BASE: CR {car.basePrice.toLocaleString()} // CURRENT: CR {total.toLocaleString()}
          </div>
        </div>

        <div className="p-6 flex-1 space-y-8">
          {CATEGORIES.map((cat) => (
            <section key={cat.key}>
              <h3 className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
                <SafeIcon icon={cat.icon} /> {cat.label}
              </h3>
              <div className="space-y-2">
                {configOptions[cat.key + 's'].map(opt => {
                  const active = config[cat.key].id === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setConfig(prev => ({ ...prev, [cat.key]: opt }))}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        active
                          ? `bg-${cat.accent}-500/10 border-${cat.accent}-500/50 box-glow-${cat.accent}`
                          : 'bg-zinc-900/40 border-zinc-800/50 hover:border-zinc-600'
                      }`}
                      style={active ? {
                        backgroundColor: `${accentHex}15`,
                        borderColor: `${accentHex}80`,
                        boxShadow: `0 0 15px ${accentHex}33, inset 0 0 20px ${accentHex}1a`
                      } : {}}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className={`font-bold text-sm ${active ? 'text-zinc-100' : 'text-zinc-300'}`}>
                          {opt.name}
                        </span>
                        <span className="font-mono text-xs text-zinc-500">
                          {opt.price > 0 ? `+${opt.price} CR` : 'STD'}
                        </span>
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="p-6 bg-zinc-950 border-t border-zinc-800/50 lg:sticky lg:bottom-0 z-20">
          <button
            onClick={() => { setDeployed(true); setTimeout(() => navigate('/highway'), 1800); }}
            disabled={deployed}
            className="w-full py-4 bg-zinc-100 text-zinc-950 font-black uppercase tracking-widest text-sm rounded hover:bg-cyan-400 hover:box-glow-cyan transition-all flex items-center justify-center gap-3 group disabled:opacity-50"
          >
            {deployed ? (
              <>
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-xl text-emerald-500" />
                Deploying Node...
              </>
            ) : (
              <>
                <SafeIcon icon={FiIcons.FiCpu} className="text-xl group-hover:animate-pulse" />
                Deploy Sovereign Node
              </>
            )}
          </button>
          <div className="mt-3 text-center text-[10px] text-zinc-600 tracking-widest uppercase">
            Sovereignty: <span className="text-emerald-400">Verified</span> // Telemetry: <span className="text-rose-400">Zero</span>
          </div>
        </div>
      </div>
    </div>
  );
}